import React, { createContext } from "react";

import useBillingSubscription from "@app/analytics/hooks/billing/useBillingSubscription";
import useAuth from "@app/common/auth/useAuth";
import {
  UNKNOWN_PLAN,
  UNKNOWN_SUBSCRIPTION,
} from "@app/common/constants/billing";
import type {
  BillingSubscription,
  BillingCurrentPlan,
  BillingScheduledPlan,
} from "@app/common/types";
import {
  getCurrentPlanFromSubscriptionAndUser,
  getScheduledPlanFromSubscription,
} from "@app/common/utils/billingUtils";

interface BillingPlanSubscriptionContextProviderProps {
  children: React.ReactNode;
}

interface BillingPlanSubscriptionContext {
  currentPlan: BillingCurrentPlan;
  isError: boolean;
  isLoading: boolean;
  scheduledPlan?: BillingScheduledPlan;
  subscription: BillingSubscription;
}

const DEFAULT_BILLING_CONTEXT: BillingPlanSubscriptionContext = {
  currentPlan: UNKNOWN_PLAN,
  isError: false,
  isLoading: true,
  subscription: UNKNOWN_SUBSCRIPTION,
};

export const BillingPlanSubscriptionContext =
  createContext<BillingPlanSubscriptionContext>(DEFAULT_BILLING_CONTEXT);

export const BillingPlanSubscriptionContextProvider = ({
  children,
}: BillingPlanSubscriptionContextProviderProps) => {
  const { user } = useAuth();
  const { subscription, isLoading, isError } = useBillingSubscription();

  const currentPlan = getCurrentPlanFromSubscriptionAndUser(subscription, user);
  const scheduledPlan = getScheduledPlanFromSubscription(subscription);

  const context: BillingPlanSubscriptionContext = {
    currentPlan,
    isError,
    isLoading,
    scheduledPlan,
    subscription,
  };

  return (
    <BillingPlanSubscriptionContext.Provider value={context}>
      {children}
    </BillingPlanSubscriptionContext.Provider>
  );
};
